import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Modal } from './Modal';
import { useAuth } from '../context/AuthContext';
import { getAccessToken } from '../utils/auth';

interface ClaimBusinessModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  businessName: string;
  onSubmitted?: () => void;
}

type ClaimStatus = 'idle' | 'submitting' | 'success' | 'error';

export function ClaimBusinessModal({
  isOpen,
  onClose,
  businessId,
  businessName,
  onSubmitted,
}: ClaimBusinessModalProps) {
  const { session } = useAuth();
  const [evidence, setEvidence] = useState('');
  const [status, setStatus] = useState<ClaimStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setEvidence('');
      setStatus('idle');
      setErrorMessage(null);
    }
  }, [isOpen]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmed = evidence.trim();
    if (!trimmed) {
      setErrorMessage('Please provide proof of ownership.');
      return;
    }

    setStatus('submitting');
    setErrorMessage(null);

    try {
      const token = await getAccessToken(session?.access_token);
      if (!token) {
        throw new Error('You must be signed in to submit a claim.');
      }

      const response = await fetch('/api/claim-business', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          businessId,
          evidence: trimmed,
        }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok || payload?.error) {
        const message = payload?.error || 'Unable to submit claim.';
        throw new Error(typeof message === 'string' ? message : 'Unable to submit claim.');
      }

      setStatus('success');
      onSubmitted?.();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to submit claim.';
      setErrorMessage(message);
      setStatus('error');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-xl">
      <div className="p-8">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-on-surface">Claim {businessName}</h2>
          <p className="text-sm text-on-surface-variant">
            Please provide proof that you own or manage this business. For example: your name and role,
            a phone number we can verify, or other identifying information.
          </p>
        </div>

        {status === 'success' ? (
          <div className="space-y-6">
            <div className="rounded-xl border border-outline-variant bg-surface-container-high p-4 text-on-surface">
              Your claim has been submitted! We will review it within 48 hours.
            </div>
            <button type="button" onClick={onClose} className="btn-primary w-full">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="claim-evidence" className="block text-sm font-medium text-on-surface-variant mb-2">
                Evidence
              </label>
              <textarea
                id="claim-evidence"
                rows={5}
                value={evidence}
                onChange={(event) => setEvidence(event.target.value)}
                className="w-full rounded-xl border border-outline bg-surface-container-high text-on-surface focus:ring-primary focus:border-primary px-3 py-2"
                placeholder="Example: I am the owner, my role is..., you can verify me at ..."
                required
              />
              <p className="mt-2 text-xs text-on-surface-variant">
                Please include any identifying details that help us verify ownership.
              </p>
            </div>

            {errorMessage && (
              <div className="rounded-xl border border-error bg-error-container p-3 text-on-error-container text-sm">
                {errorMessage}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                className="btn-primary flex items-center justify-center gap-2 w-full"
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Claim'
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full px-4 py-2 font-medium border border-outline text-on-surface-variant hover:bg-surface-container-high transition-colors duration-[var(--md-sys-motion-duration-short3)]"
                disabled={status === 'submitting'}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}
